import { Controller, useFormContext, useFormState } from 'react-hook-form';

import { FormControl, TextField, useTheme } from 'src/components/mui';

import { TInputProps } from './types';
// eslint-disable-next-line import/no-extraneous-dependencies
import { get } from 'lodash';
import { FormHelperText as FormHelperTextMUI } from '@mui/material';
import { PhoneInput } from './PhoneInput';
import { FC } from 'react';

type FormHelperTextProps = {
  children?: any;
  sx?: any;
};

const FormHelperText: FC<FormHelperTextProps> = ({ children, ...props }) => (
  <FormHelperTextMUI {...props}>{children}</FormHelperTextMUI>
);

export const Input = ({
  name,
  label,
  helperText,
  type,
  maskFn,
  tooltip,
  rtl,
  step = '1',
  height = '56px',
  sx,
  ...other
}: TInputProps) => {
  const { setValue } = useFormContext();
  const { errors } = useFormState();
  const theme = useTheme();
  const errorMessage = get(errors, `${name}.message`);
  const primaryColor = theme.palette.primary.main;
  const errorColor = theme.palette.error.main;

  return (
    <Controller
      name={name}
      render={({ field }) => (
        <>
          {type === 'tel' ? (
            <FormControl
              error={!!errorMessage}
              sx={{
                mr: { xs: 0, md: rtl ? '16px !important' : '0 !important' },
                width: '100%',
                '&>div>.form-control': {
                  width: '100% !important',
                  color: '#fff',
                  border: `1px solid ${!!errorMessage ? errorColor : '#fff'}`,
                  borderRadius: '4px',
                  background: 'transparent',
                  height,
                  padding: rtl
                    ? '18.5px 45px 18.5px 14px !important'
                    : '18.5px 14px 18.5px 45px !important',
                  '&:focus': {
                    boxShadow: `0 0 0 1px ${primaryColor}`
                  },
                  '&:hover': {
                    borderColor: primaryColor
                  }
                },
                '&>div>.special-label': {
                  background: 'transparent',
                  backdropFilter: 'blur(5px)',
                  left: rtl ? 'calc(100% - 55px) !important' : '10px !important',
                  top: '-9px !important',
                  color: '#fff !important'
                },
                '&>div>.flag-dropdown>.country-list': {
                  color: 'white',
                  background: 'transparent',
                  backdropFilter: 'blur(35px)',
                  borderRadius: '5px',
                  '&>.country:hover, &>.country.highlight': {
                    background: 'rgba(0,0,0,.2) !important'
                  },
                  '&>.country': {
                    textAlign: rtl ? 'right' : 'left'
                  },
                  '&>.country>.dial-code': {
                    color: 'white'
                  }
                },
                '&>div>.flag-dropdown>.selected-flag': {
                  ...(rtl
                    ? {
                        padding: '0 11px 0 0 !important'
                      }
                    : {}),

                  '.arrow': {
                    display: 'none !important'
                  }
                },
                ...sx
              }}
            >
              <PhoneInput
                country={'us'}
                onBlur={field.onBlur}
                value={field.value}
                name={field.name}
                inputRef={field.ref}
                autoComplete="phone_number"
                onChange={(value) => field.onChange(value)}
              />
              {errorMessage && (
                <FormHelperText sx={{ textAlign: rtl ? 'right' : 'left' }}>
                  {errorMessage}
                </FormHelperText>
              )}
            </FormControl>
          ) : (
            <TextField
              {...field}
              rtl={rtl}
              fullWidth
              label={label}
              onChange={(event) => {
                field.onChange(event);
                if (maskFn) {
                  setValue(name, maskFn(event.target.value));
                }
              }}
              error={!!errorMessage}
              helperText={!!errorMessage ? errorMessage : helperText}
              type={type}
              inputProps={{
                step
              }}
              sx={{
                height,
                '& .MuiInputBase-input': {
                  height: '100%'
                },
                '& .MuiInputBase-root': {
                  height: '100%'
                },
                mb: !!errorMessage ? 2 : 0,
                ...sx
              }}
              {...other}
            />
          )}
        </>
      )}
    />
  );
};
